import { supabase } from './supabase'
import type { User, AuthState } from '@/types'

export class AuthService {
  async signUp(email: string, password: string, name?: string) {
    console.log('🔐 AuthService.signUp called')
    console.log('🔐 Email:', email)
    console.log('🔐 Password length:', password?.length || 0)
    console.log('🔐 Name:', name)
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name: name || ''
        }
      }
    })

    if (error) {
      console.error('❌ Auth signUp error:', error)
      console.error('❌ Error name:', error.name)
      console.error('❌ Error message:', error.message)
      console.error('❌ Error status:', error.status)
      throw error
    }
    
    console.log('✅ Sign up successful:', data.user?.email)
    return data
  }

  async signIn(email: string, password: string) {
    console.log('🔐 AuthService.signIn called')
    console.log('🔐 Email:', email)
    console.log('🔐 Password length:', password?.length || 0)
    
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    })

    if (error) {
      console.error('❌ Auth signIn error:', error)
      console.error('❌ Error name:', error.name)
      console.error('❌ Error message:', error.message)
      console.error('❌ Error status:', error.status)
      throw error
    }
    
    console.log('✅ Sign in successful:', data.user?.email)
    return data
  }

  async signOut() {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  }

  async signInWithGoogle() {
    console.log('🔐 AuthService.signInWithGoogle called')
    
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        }
      }
    })

    if (error) {
      console.error('❌ Google sign in error:', error)
      throw error
    }

    console.log('✅ Google sign in initiated')
    return data
  }

  async signInWithFacebook() {
    console.log('🔐 AuthService.signInWithFacebook called')
    
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'facebook',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`
      }
    })

    if (error) {
      console.error('❌ Facebook sign in error:', error)
      throw error
    }

    console.log('✅ Facebook sign in initiated')
    return data
  }

  async getCurrentUser(): Promise<User | null> {
    console.log('🔐 getCurrentUser called')
    const { data: { user } } = await supabase.auth.getUser()
    
    if (!user) {
      console.log('🔐 No user found')
      return null
    }

    console.log('🔐 User found:', user.email)

    // Get additional user data from profiles table
    const { data: profile } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single()

    console.log('🔐 Profile data:', profile ? 'found' : 'not found')

    return {
      id: user.id,
      email: user.email!,
      name: profile?.name || user.user_metadata?.name || '',
      avatar: profile?.avatar_url,
      isPremium: profile?.is_premium || false,
      createdAt: new Date(user.created_at)
    }
  }

  async getCurrentSession() {
    console.log('🔐 getCurrentSession called')
    const session = await supabase.auth.getSession()
    console.log('🔐 Session:', session.data.session ? 'exists' : 'null')
    return session
  }

  async updateProfile(updates: Partial<Pick<User, 'name' | 'avatar'>>) {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('No user logged in')

    const { error } = await supabase
      .from('profiles')
      .upsert({
        id: user.id,
        ...updates,
        updated_at: new Date().toISOString()
      })

    if (error) throw error
  }

  onAuthStateChange(callback: (state: AuthState) => void) {
    console.log('🔐 Setting up auth state change listener...')
    
    return supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔐 Auth state changed:', event)
      console.log('🔐 Session:', session ? 'exists' : 'null')
      console.log('🔐 User:', session?.user?.email || 'none')
      
      const user = session?.user ? await this.getCurrentUser() : null
      
      console.log('🔐 Calling callback with user:', user?.email || 'null')
      
      callback({
        user,
        isAuthenticated: !!session?.user,
        isLoading: false
      })
    })
  }

  // MFA/2FA Methods
  async enrollMFA() {
    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: 'totp',
      friendlyName: 'Authenticator App'
    })

    if (error) throw error

    return {
      id: data.id,
      qrCode: data.totp.qr_code,
      secret: data.totp.secret,
      uri: data.totp.uri
    }
  }

  async verifyMFAEnrollment(factorId: string, code: string) {
    const { data, error } = await supabase.auth.mfa.verify({
      factorId,
      code
    })

    if (error) throw error
    return data
  }

  async challengeMFA(factorId: string) {
    const { data, error } = await supabase.auth.mfa.challenge({
      factorId
    })

    if (error) throw error
    return data
  }

  async verifyMFAChallenge(factorId: string, challengeId: string, code: string) {
    const { data, error } = await supabase.auth.mfa.verify({
      factorId,
      challengeId,
      code
    })

    if (error) throw error
    return data
  }

  async unenrollMFA(factorId: string) {
    const { data, error } = await supabase.auth.mfa.unenroll({
      factorId
    })

    if (error) throw error
    return data
  }

  async getMFAFactors() {
    const { data: { user } } = await supabase.auth.getUser()
    
    if (!user) return []

    // Get all enrolled MFA factors
    const factors = user.factors || []
    return factors
  }
}

export const authService = new AuthService()
